/**
 * User slice - single source for users API
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { toast } from 'react-toastify';
import { USERS, ROLES, PERMISSIONS, STATUSES, NOTIFICATIONS } from '../../api/endpoints';

const errorMessage = (err, fallback) =>
  err.response?.data?.message ||
  (err.response?.status === 400 ? 'Invalid request.' : fallback);

export const getUsersList = createAsyncThunk(
  'users/getUsersList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/users', { params });
      return data?.data ?? data;
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to fetch users.'));
      return rejectWithValue(errorMessage(err, 'Failed to fetch users.'));
    }
  }
);

export const getUserById = createAsyncThunk(
  'users/getUserById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(USERS.BY_ID(id));
      return data?.data ?? data;
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to fetch user.'));
      return rejectWithValue(errorMessage(err, 'Failed to fetch user.'));
    }
  }
);

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(USERS.LIST, userData);
      return data;
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to create user.'));
      return rejectWithValue(errorMessage(err, 'Failed to create user.'));
    }
  }
);

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/users/${id}`, userData);
      return data?.data ?? data;
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to update user.'));
      return rejectWithValue(errorMessage(err, 'Failed to update user.'));
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(USERS.BY_ID(id));
      return id;
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to delete user.'));
      return rejectWithValue(errorMessage(err, 'Failed to delete user.'));
    }
  }
);

export const getUserStatus = createAsyncThunk(
  'users/getUserStatus',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('statuses?conditions[model]=User');
      return data?.data?.list ?? data?.list ?? [];
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to fetch user statuses.'));
      return rejectWithValue(errorMessage(err, 'Failed to fetch user statuses.'));
    }
  }
);

export const getRoles = createAsyncThunk(
  'users/getRoles',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(ROLES.LIST);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch roles');
    }
  }
);

export const getNotifications = createAsyncThunk(
  'users/getNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/notifications/unread');
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch notifications');
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'users/markNotificationAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(NOTIFICATIONS.BY_ID(notificationId));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to mark notification as read');
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'users/markAllNotificationsAsRead',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.patch('/notifications/mark-all-read');
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to mark all notifications as read'
      );
    }
  }
);

export const getUsersByRole = createAsyncThunk(
  'users/getUsersByRole',
  async (role, { rejectWithValue }) => {
    try {
      const { data } = await api.get(USERS.BY_ROLE(role));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch users by role');
    }
  }
);

export const updateUserDirectPermissions = createAsyncThunk(
  'users/updateUserDirectPermissions',
  async ({ userId, permissions }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/users/${userId}/direct-permissions`, { permissions });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update permissions');
    }
  }
);

export const getAllPermissions = createAsyncThunk(
  'users/getAllPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(PERMISSIONS.LIST);
      return data?.data ?? data ?? [];
    } catch (err) {
      if (err.response?.status === 403) return [];
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch permissions');
    }
  }
);

export const getPermissionsByRole = createAsyncThunk(
  'users/getPermissionsByRole',
  async (roleId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/roles/${roleId}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch role permissions');
    }
  }
);

export const updateRolePermissions = createAsyncThunk(
  'users/updateRolePermissions',
  async ({ roleId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(ROLES.BY_ID(roleId), payload);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update role permissions');
    }
  }
);

const initialState = {
  users: null,
  currentUser: null,
  userStatuses: [],
  roles: null,
  usersByRole: null,
  allPermissions: [],
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
    clearUsersError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsersList.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(getUserStatus.fulfilled, (state, action) => {
        state.userStatuses = action.payload;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.roles = action.payload;
      })
      .addCase(getUsersByRole.fulfilled, (state, action) => {
        state.usersByRole = action.payload;
      })
      .addCase(getAllPermissions.fulfilled, (state, action) => {
        state.allPermissions = action.payload;
      })
      .addMatcher(
        (a) =>
          [
            getUsersList.pending,
            getUserById.pending,
            createUser.pending,
            updateUser.pending,
            deleteUser.pending,
          ].some((t) => t.match(a)),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          [
            getUsersList.rejected,
            getUserById.rejected,
            createUser.rejected,
            updateUser.rejected,
            deleteUser.rejected,
          ].some((t) => t.match(a)),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || 'Something went wrong';
        }
      )
      .addMatcher(
        (a) => [createUser.fulfilled, updateUser.fulfilled, deleteUser.fulfilled].some((t) => t.match(a)),
        (state) => {
          state.loading = false;
        }
      );
  },
});

export const { clearCurrentUser, clearUsersError } = userSlice.actions;
export default userSlice.reducer;
